import { pushToast, numberedSteps, button } from "../../design/components.js";
import { safetyStepKeys } from "./safety-steps.js";
import type { AlertEvent } from "./alert-model.js";

type T = (k: string, p?: Record<string, string | number>) => string;
const KIND_TITLE: Record<string, string> = {
  new: "alert.toast.new", escalation: "alert.toast.escalation",
  inPath: "alert.toast.inPath", reAlert: "alert.toast.reAlert", allClear: "alert.toast.allClear",
};

function titleFor(t: T, e: AlertEvent, place: string): string {
  const hazard = t(`hazard.${e.hazard}`);
  const base = t(KIND_TITLE[e.kind] ?? "alert.toast.new", { hazard, place });
  return e.groupCount && e.groupCount > 1
    ? `${base} · ${t("now.group.areas", { n: e.groupCount })}`
    : base;
}

export interface ToastOpts { t: T; event: AlertEvent; placeName?: string; onTap?: (subject: string) => void; autoDismissMs?: number; }
export interface ToastHandle { dismiss(): void; el: HTMLElement; }

/** Builds the alert toast element (▲ glyph, change-named title) without any lifecycle —
 *  no append, no click, no timeout. Callers (the ToastStack) own placement and dismissal. */
export function buildPushToast(opts: Pick<ToastOpts, "t" | "event" | "placeName">): HTMLElement {
  const place = opts.placeName ?? opts.t("now.place.default");
  const title = titleFor(opts.t, opts.event, place);
  const body = opts.event.kind === "allClear"
    ? opts.t("alert.toast.tap")
    : opts.t("alert.toast.sub", { distance: opts.event.distanceKm.toFixed(1), place });
  const el = pushToast({ title, body });
  el.setAttribute("data-toast", opts.event.subject);
  el.setAttribute("role", "status");
  return el;
}

/** In-app PushToast (GUI §6 alert receipt): ▲ glyph, change-named title, tap → IncidentDetail.
 *  The toast names what changed via the event kind. Foreground channel — no registration.
 *  Self-managing variant (append + click-dismiss + timeout) for one-off callers and tests;
 *  bursty alert paths go through ToastStack via buildPushToast instead. */
export function showPushToast(host: HTMLElement, opts: ToastOpts): ToastHandle {
  const el = buildPushToast(opts);
  el.tabIndex = 0;
  const dismiss = () => el.remove();
  el.addEventListener("click", () => { opts.onTap?.(opts.event.subject); dismiss(); });
  host.append(el);
  const ms = opts.autoDismissMs ?? 8000;
  if (ms > 0 && typeof setTimeout !== "undefined") setTimeout(dismiss, ms); // wallclock-allow: browser UI cadence (apps/webapp is gate-30 excluded)
  return { dismiss, el };
}

export interface TakeoverOpts { t: T; event: AlertEvent; placeName?: string; onDismiss?: () => void; onDetails?: (subject: string) => void; queued?: number; }

/** Dismiss-button label: plain "Dismiss" alone, or "Dismiss & see next · N more" when
 *  more criticals are queued behind this one. */
export function takeoverDismissLabel(t: T, queued = 0): string {
  return queued > 0 ? t("alert.takeover.dismissNext", { n: queued }) : t("alert.takeover.dismiss");
}

/** Full-screen critical takeover (GUI §6): critical severity always breaks through with
 *  one-tap safety steps. Facts are tone-invariant. */
export function criticalTakeover(opts: TakeoverOpts): HTMLElement {
  const { t, event } = opts;
  const place = opts.placeName ?? t("now.place.default");
  const el = document.createElement("div");
  el.className = "critical-takeover";
  el.setAttribute("role", "alertdialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("data-takeover", event.subject);

  const sev = document.createElement("div");
  sev.className = "takeover-sev bx-microlabel";
  sev.textContent = t("alert.severity.critical");
  el.append(sev);

  const h = document.createElement("h1");
  h.className = "bx-display";
  const titleBase = t("alert.takeover.title", { hazard: t(`hazard.${event.hazard}`) });
  h.textContent = event.groupCount && event.groupCount > 1
    ? `${titleBase} · ${t("now.group.areas", { n: event.groupCount })}`
    : titleBase;
  el.append(h);

  const sub = document.createElement("p");
  sub.className = "bx-body";
  sub.textContent = t("alert.takeover.sub", { distance: event.distanceKm.toFixed(1), place });
  el.append(sub);

  const stepsLabel = document.createElement("div");
  stepsLabel.className = "bx-microlabel";
  stepsLabel.textContent = t("alert.takeover.steps");
  el.append(stepsLabel);
  el.append(numberedSteps(safetyStepKeys(event.hazard).map((k) => t(k)), 0));

  const actions = document.createElement("div");
  actions.className = "takeover-actions";
  const details = button({ label: t("alert.takeover.details"), variant: "secondary", onClick: () => opts.onDetails?.(event.subject) });
  details.setAttribute("data-action", "details");
  const dismiss = button({ label: takeoverDismissLabel(t, opts.queued), variant: "primary", onClick: () => { el.remove(); opts.onDismiss?.(); } });
  dismiss.setAttribute("data-action", "dismiss");
  actions.append(details, dismiss);
  el.append(actions);
  return el;
}
